"use client";

/**
 * Where popovers and dropdown menus open, for a whole region of the screen.
 *
 * Right-edge columns (inbox task, file detail): pickers open LEFT of the
 * field, top-aligned — one region placement, not per-field `side`. Shift for
 * vertical room; never flip above/below (late content must not jump).
 *
 * Region wins over component `side`/`align`. Dialogs reset to a new surface.
 */

import * as React from "react";

export interface OverlayPlacement {
  readonly side: "top" | "right" | "bottom" | "left";
  readonly align: "start" | "center" | "end";
  readonly sideOffset: number;
}

/** Right-edge details column: beside field, left, top edges aligned. */
export const OVERLAY_PLACEMENT_BESIDE_LEFT: OverlayPlacement = {
  side: "left",
  align: "start",
  sideOffset: 8,
};

const OverlayPlacementContext = React.createContext<OverlayPlacement | null>(
  null,
);

export function OverlayPlacementProvider({
  placement,
  children,
}: {
  /** `null` resets to each component's own placement (dialogs). */
  readonly placement: OverlayPlacement | null;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <OverlayPlacementContext.Provider value={placement}>
      {children}
    </OverlayPlacementContext.Provider>
  );
}

export function useOverlayPlacement(): OverlayPlacement | null {
  return React.useContext(OverlayPlacementContext);
}
